import { REPORT_VARIABLE_HELP_TEXT } from '../../../utils/report-variable-help-text';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  ViewChildren,
  QueryList,
  ViewEncapsulation,
} from '@angular/core';
import { REPORT_PERIODS, formatReportDate, ReportPeriodSelection } from '../../../utils/report-period.utils';
import { DxFormComponent } from 'devextreme-angular';
import { LOCAL_DATE_SERIALIZATION_FORMAT } from 'ag-common-lib/lib/constants/date.constants';
import { Constants, EditorOptions } from 'ag-common-lib/lib/constants/validation.constants';
import {
  ReportId,
  ReportInputParameterKeys,
  type AgencyBrandingOption,
  type AgencyBrandingOptionValue,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import { LookupKeys } from 'ag-common-lib/public-api';
import { toAgencyBrandingOptionValue } from '../../../utils/agency-branding-options.utils';
import type { ProductionReportConfig, ProductionReportVariable } from '../reports-core/production-reports.models';

export interface ReportSettingsFormApplyEvent {
  isValid: boolean;
}

export interface ReportSettingsTemplateTextChangeEvent {
  field: 'title' | 'subtitle';
  value: string;
}

export interface ReportSettingsVariableChangeEvent {
  variable: ProductionReportVariable;
  value: unknown;
}

interface ReportSettingsVariableFieldConfig {
  variable: ProductionReportVariable;
  dataField: string;
  editorType: 'dxNumberBox' | 'dxDateBox' | 'dxTextBox' | 'dxSwitch' | 'dxSelectBox';
  editorOptions: Record<string, unknown>;
  cssClass?: string;
  helpText?: string;
}


@Component({
  selector: 'ag-shr-report-settings-form',
  templateUrl: './report-settings-form.component.html',
  styleUrls: ['./report-settings-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'report-settings-form' },
})
export class ReportSettingsFormComponent implements OnChanges {
  @ViewChildren('settingsFormRef') private settingsForms?: QueryList<DxFormComponent>;

  /** Pre-populated report type / id (used by consumers to drive routing) */
  @Input() reportType: ReportId | null = null;

  /** Pre-populated default start date (ISO string, e.g. '2025-01-01') */
  @Input() defaultStartDate: string | null = null;

  /** Pre-populated default end date (ISO string, e.g. '2025-12-31') */
  @Input() defaultEndDate: string | null = null;

  @Input() formData: Record<string, unknown> = {};
  /** ARM AG reports and Portal MGA/RMD reports offer the picker. */
  @Input() showBranding = true;
  @Input() brandingOptions: AgencyBrandingOption[] = [];
  @Input() selectedReport: ProductionReportConfig | null = null;
  @Input() isLoading = false;
  @Input() periodSelection: ReportPeriodSelection | null = null;
  @Input() dataAvailableThrough: string | null = null;
  @Input() dataAvailableFrom: string | null = null;
  @Input() paidReport = false;
  @Input() periodInvalid = false;
  @Output() readonly periodSelectionChange = new EventEmitter<ReportPeriodSelection>();

  @Output() readonly apply = new EventEmitter<void>();
  @Output() readonly resetToDefaults = new EventEmitter<void>();
  @Output() readonly templateTextChange = new EventEmitter<ReportSettingsTemplateTextChangeEvent>();
  @Output() readonly rowsPerPageChange = new EventEmitter<unknown>();
  @Output() readonly brandingIdChange = new EventEmitter<AgencyBrandingOptionValue>();
  @Output() readonly variableChange = new EventEmitter<ReportSettingsVariableChangeEvent>();

  protected readonly LookupKeys = LookupKeys;
  protected readonly LOCAL_DATE_SERIALIZATION_FORMAT = LOCAL_DATE_SERIALIZATION_FORMAT;
  protected readonly Constants = Constants;
  protected readonly EditorOptions = EditorOptions;
  protected readonly ReportInputParameterKeys = ReportInputParameterKeys;
  /** Keeps the threshold and its switch on one row even where dx-form would stack columns. */
  protected readonly thresholdRowColCountByScreen = { xs: 6, sm: 6 };
  private readonly selectedReportSig = signal<ProductionReportConfig | null>(null);
  private readonly periodEnabledSig = signal(false);
  private readonly isThresholdIncludedSig = signal(true);

  protected titleEditorOptions: Record<string, unknown> = {};
  protected subtitleEditorOptions: Record<string, unknown> = {};
  protected rowsPerPageEditorOptions: Record<string, unknown> = {};
  protected brandingIdEditorOptions: Record<string, unknown> = {};

  protected readonly variableFieldConfigsSig = computed<ReportSettingsVariableFieldConfig[]>(() =>
    (this.selectedReportSig()?.variables ?? [])
      // Rendered on the threshold's row instead, see includeThresholdFieldConfigSig.
      .filter(variable => variable.name !== ReportInputParameterKeys.includeThreshold)
      .filter(
        variable =>
          !this.periodEnabledSig() ||
          (variable.name !== ReportInputParameterKeys.startDate && variable.name !== ReportInputParameterKeys.endDate),
      )
      .map(variable => this.buildVariableFieldConfig(variable)),
  );

  /** The on/off switch that sits beside Display Threshold, when the report has one. */
  protected readonly includeThresholdFieldConfigSig = computed<ReportSettingsVariableFieldConfig | null>(() => {
    const variable = this.findVariable(ReportInputParameterKeys.includeThreshold);

    return variable
      ? {
          ...this.buildVariableFieldConfig(variable),
          editorOptions: {
            ...this.buildVariableEditorOptions(variable),
            switchedOffText: 'Off',
            switchedOnText: 'On',
            hint: 'Turn off to list every result, whatever the threshold',
          },
          cssClass: 'ag-inline-switch',
        }
      : null;
  });

  /**
   * Kept apart from variableFieldConfigsSig so flipping the switch rebuilds only this
   * editor. Disabled rather than hidden, so the switch keeps its place on the row.
   */
  protected readonly thresholdEditorOptionsSig = computed<Record<string, unknown>>(() => {
    const variable = this.findVariable(ReportInputParameterKeys.displayThreshold);

    return variable ? { ...this.buildVariableEditorOptions(variable), disabled: !this.isThresholdIncludedSig() } : {};
  });

  protected periodOpen = true;
  protected layoutOpen = false;
  protected displayOpen = false;

  protected get periodSummary(): string {
    const selection = this.periodSelection;
    if (!selection) return '';
    const mode = REPORT_PERIODS.find(item => item.value === selection.period)?.description ?? '';
    if (selection.period === 'custom' && selection.startDate && selection.endDate)
      return `${mode} · ${formatReportDate(selection.startDate)}–${formatReportDate(selection.endDate)}`;
    return selection.endDate ? `${mode} · through ${formatReportDate(selection.endDate)}` : mode;
  }

  protected get layoutSummary(): string {
    const branding = this.showBranding
      ? this.brandingOptions.find(item => item.value === this.formData['brandingId'])?.description
      : null;
    return [branding, `${this.formData['rowsPerPage'] ?? '—'} rows`].filter(Boolean).join(' · ');
  }

  protected get displaySummary(): string {
    return this.variableFieldConfigsSig()
      .map(config => {
        const value = this.formData[config.dataField];
        if (config.variable.name === ReportInputParameterKeys.showNtpSeparately)
          return value ? 'NTP separate' : 'NTP included';
        if (config.variable.name === ReportInputParameterKeys.displayThreshold)
          return this.formData[`variable_${ReportInputParameterKeys.includeThreshold}`] === false
            ? 'Threshold off'
            : `Threshold ${value ?? '—'}`;
        const displayValue = config.variable.options?.find(option => option.value === value)?.label ?? value;
        return `${config.variable.label}: ${typeof displayValue === 'boolean' ? (displayValue ? 'Yes' : 'No') : (displayValue ?? '—')}`;
      })
      .join(' · ');
  }

  protected toggleSection(section: 'period' | 'layout' | 'display'): void {
    if (section === 'period') this.periodOpen = !this.periodOpen;
    if (section === 'layout') this.layoutOpen = !this.layoutOpen;
    if (section === 'display') this.displayOpen = !this.displayOpen;
  }

  constructor() {
    this.buildEditorOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.periodEnabledSig.set(this.periodSelection !== null);
    if (changes['selectedReport']) {
      this.selectedReportSig.set(this.selectedReport);
    }

    if (changes['formData']) {
      this.isThresholdIncludedSig.set(
        this.formData?.[`variable_${ReportInputParameterKeys.includeThreshold}`] !== false,
      );
    }

    if (changes['brandingOptions']) {
      this.brandingIdEditorOptions = this.buildBrandingIdEditorOptions();
    }

    if (changes['defaultStartDate'] || changes['defaultEndDate'] || changes['reportType']) {
      // Inputs available for consumer use — lifecycle hook kept for future reactive needs
    }
  }

  protected onSubmit(event: Event): void {
    event.preventDefault();
    const invalid = this.settingsForms?.toArray().some(form => !form.instance.validate().isValid);
    if (invalid) {
      this.layoutOpen = true;
      this.displayOpen = true;
      return;
    }
    if (!this.periodInvalid) this.apply.emit();
  }

  protected getVariableHelpText(label: unknown): string {
    const variable = this.selectedReportSig()?.variables.find(item => item.label === label);

    return variable ? REPORT_VARIABLE_HELP_TEXT[variable.name] ?? '' : '';
  }

  private findVariable(name: ReportInputParameterKeys): ProductionReportVariable | undefined {
    return this.selectedReportSig()?.variables.find(variable => variable.name === name);
  }

  private buildVariableFieldConfig(variable: ProductionReportVariable): ReportSettingsVariableFieldConfig {
    return {
      variable,
      dataField: `variable_${String(variable.name)}`,
      editorType: this.getVariableEditorTypeValue(variable),
      // Floating editor labels do not render dx-form label templates.
      editorOptions: {
        ...this.buildVariableEditorOptions(variable),
        ...(variable.name === ReportInputParameterKeys.ntpDisplayMode ? { labelMode: 'hidden' } : {}),
      },
      cssClass:
        variable.name === ReportInputParameterKeys.ntpDisplayMode
          ? 'production-report-viewer__ntp-help-field'
          : variable.type === 'boolean' ? 'ag-switch-with-label' : undefined,
      helpText: REPORT_VARIABLE_HELP_TEXT[variable.name],
    };
  }

  private getVariableEditorTypeValue = (
    variable: ProductionReportVariable,
  ): 'dxNumberBox' | 'dxDateBox' | 'dxTextBox' | 'dxSwitch' | 'dxSelectBox' => {
    if (variable.type === 'number') return 'dxNumberBox';
    if (variable.type === 'date') return 'dxDateBox';
    if (variable.type === 'boolean') return 'dxSwitch';
    if (variable.type === 'select') return 'dxSelectBox';
    return 'dxTextBox';
  };

  private buildVariableEditorOptions = (variable: ProductionReportVariable): Record<string, unknown> => {
    const onValueChanged = (event: { value: unknown }) => {
      this.variableChange.emit({ variable, value: event.value });
    };

    if (variable.type === 'number') {
      const base: Record<string, unknown> = { showSpinButtons: true, onValueChanged };
      if (variable.name === ReportInputParameterKeys.displayThreshold) {
        return { ...base, ...EditorOptions.CURRENCY };
      }
      return base;
    }

    if (variable.type === 'date') {
      return {
        type: 'date',
        displayFormat: Constants.DISPLAY_DATE_FORMAT,
        dateSerializationFormat: LOCAL_DATE_SERIALIZATION_FORMAT,
        useMaskBehavior: true,
        onValueChanged,
      };
    }

    if (variable.type === 'boolean') {
      return {
        location: 'right',
        switchedOffText: 'No',
        switchedOnText: 'Yes',
        onValueChanged,
      };
    }

    if (variable.type === 'select') {
      return {
        items: variable.options ?? [],
        valueExpr: 'value',
        displayExpr: 'label',
        showClearButton: false,
        onValueChanged,
      };
    }

    return { valueChangeEvent: 'input', onValueChanged };
  };

  private buildEditorOptions(): void {
    this.titleEditorOptions = {
      labelMode: 'static',
      placeholder: 'Default',
      valueChangeEvent: 'input',
      onValueChanged: (event: { value: unknown }) => {
        this.templateTextChange.emit({ field: 'title', value: String(event.value ?? '') });
      },
    };

    this.subtitleEditorOptions = {
      labelMode: 'static',
      placeholder: 'Default',
      valueChangeEvent: 'input',
      onValueChanged: (event: { value: unknown }) => {
        this.templateTextChange.emit({ field: 'subtitle', value: String(event.value ?? '') });
      },
    };

    this.rowsPerPageEditorOptions = {
      min: 1,
      showSpinButtons: true,
      onValueChanged: (event: { value: unknown }) => {
        this.rowsPerPageChange.emit(event.value);
      },
    };

    this.brandingIdEditorOptions = this.buildBrandingIdEditorOptions();
  }

  private buildBrandingIdEditorOptions(): Record<string, unknown> {
    return {
      items: this.brandingOptions,
      valueExpr: LookupKeys.value,
      displayExpr: LookupKeys.description,
      searchEnabled: true,
      searchExpr: LookupKeys.description,
      showClearButton: false,
      noDataText: 'No branding available',
      onValueChanged: (event: { value: unknown }) => {
        const brandingId = toAgencyBrandingOptionValue(event.value);
        if (brandingId === null) {
          return;
        }

        this.brandingIdChange.emit(brandingId);
      },
    };
  }
}
